import { App } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    OrderType,
    PaymentForm,
    ProductOrderItem,
    TaskRejectProducts,
} from "tm-shared/types";

import { ROUTES } from "@/constants";
import {
    useAppDispatch,
    useOfflineModeSelector,
    usePointPriceListSelector,
    usePointSelector,
} from "@/hooks/redux";
import {
    makeCommand,
    useMakeCommand,
} from "@/hooks/requestQueue/useRequestQueue";
import { buildOfflineCheckoutOrder } from "@/pages/WorkPlan/Tasks/utils";
import { enqueueRequestQueueItem } from "@/store/slices/offline";
import { clearTaskPayload } from "@/store/slices/progress";
import { addItemToCheckout, setTaskCompleted } from "@/store/slices/work";

interface UseTaskCompletionArgs {
    task: TaskRejectProducts;
    pointId: string;
    rejectProductsList: Record<string, ProductOrderItem>;
}

export const useTaskCompletion = ({
    task,
    pointId,
    rejectProductsList,
}: UseTaskCompletionArgs) => {
    const { notification } = App.useApp();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const taskId = task.id;
    const orderId = task?.context?.orderId;

    const { makeTaskCompleteCommands } = useMakeCommand();

    const point = usePointSelector(pointId);
    const offlineMode = useOfflineModeSelector();
    const currentPriceList = usePointPriceListSelector(pointId);

    const [loading, setLoading] = useState(false);

    const getProductsToReturn = () => {
        return Object.values(rejectProductsList)
            .filter((product) => product.count > 0)
            .map((p) => ({
                ...p,
                price: 0,
            }));
    };

    const handleConfirm = async () => {
        if (!pointId || !taskId || !orderId || !point) {
            notification.error({
                title: "Неможливо виконати задачу — відсутні параметри",
            });
            return;
        }

        setLoading(true);

        const productsToReturn = getProductsToReturn();

        if (productsToReturn.length === 0) {
            notification.error({
                title: "Ви не вказали продуктів для повернення",
            });
            setLoading(false);

            return;
        }

        if (offlineMode) {
            const checkout = buildOfflineCheckoutOrder({
                currentPriceList: currentPriceList ?? {},
                paymentForm: PaymentForm.Cash2,
                orderType: OrderType.R,
                point,
                orderId: orderId,
                productsArr: productsToReturn,
            });

            dispatch(
                addItemToCheckout({
                    checkoutList: {
                        [orderId]: checkout,
                    },
                }),
            );
        }

        const orderSaveParams = {
            products: productsToReturn,
            orderId,
            paymentForm: PaymentForm.Cash2,
        };

        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: {
                taskId,
                hasPhoto: false,
                payload: orderSaveParams,
            },
        });

        dispatch(
            enqueueRequestQueueItem({
                id: taskId,
                metadata: { pointId, taskId, orderId },
                options: {
                    todayOnly: true,
                },
                commands: [
                    makeCommand({
                        type: "postOrderSaveOne",
                        title: "Збереження заявки",
                        params: orderSaveParams,
                    }),

                    ...taskCompleteCommands,

                    makeCommand({
                        type: "getPointInventoryGetThunk",
                        title: "Синхронізація залишків",
                        params: { pointId },
                    }),
                ],
            }),
        );

        dispatch(clearTaskPayload({ taskId }));
        dispatch(setTaskCompleted({ targetId: pointId, taskId }));
        navigate(ROUTES.WorkPlan, { replace: true });

        setLoading(false);
    };

    return { loading, handleConfirm };
};
